import { EcoHubTechUser } from '@/models/ecoHubTechUser'
import { DxpServiceProviderConfigurationService } from '@/services'
import { DxpInsurerListService } from '@/services/dxpInsurerList'
import store from '@/store/store'
import pick from 'lodash/pick'
import { ContactType, DxpCustomerNameMatch } from '@/enums/graphql'
import { getSimilarityLevel } from '@/helpers/similarity'

export function getAgreementPermissions (serviceProviderId) {
    if (store.state.dxpInsurerListActiveAgreements) {
        let permissionScore = 0
        if (store.state.dxpInsurerListActiveAgreements.download_permissions_granted.includes(serviceProviderId)) permissionScore++
        if (store.state.dxpInsurerListActiveAgreements.upload_permissions_granted.includes(serviceProviderId)) permissionScore++

        // TODO improvement @TFU: Extract enums and refactor if used elsewhere.
        switch (permissionScore) {
            case 0:
                return 'NO_PERMISSIONS'
            case 1:
                return 'PARTIAL_PERMISSIONS'
            case 2:
                return 'FULL_PERMISSIONS'
        }
    } else {
        return null
    }
}

export function loadEcoHubAndDxpBaseData () {
    const promises = []

    if (store.state.user?.aclPermissions?.includes('EcoHubTechUser:read')) {
        promises.push(EcoHubTechUser.service.get().then(ecoHubTechUser => {
            store.dispatch('setEcoHubTechUser', ecoHubTechUser)
        }).catch(() => {})) // Catch error, so it doesn't get logged to the console.
    }

    if ([
        'PremiumInvoice:manage',
        'PremiumReminder:manage',
        'Commissions:manage',
    ].some(feature => store.state.user?.aclPermissions?.includes(feature))) {
        promises.push(DxpInsurerListService.get({ hasActiveAgreement: true }).then(insurerList => {
            store.dispatch('setDxpInsurerListActiveAgreements', insurerList)
        }).catch(() => {
        })) // Catch error, so it doesn't get logged to the console.
    }

    if (store.state.user?.aclPermissions?.includes('DxpServiceProviderConfiguration:read')) {
        promises.push(DxpServiceProviderConfigurationService.all().then(response => {
            const dxpServiceProviderConfigurationDisplayNames = {}
            response.data.forEach(dxpServiceProviderConfiguration => {
                dxpServiceProviderConfigurationDisplayNames[dxpServiceProviderConfiguration.service_provider_id] = dxpServiceProviderConfiguration.service_provider_display_name;
            })
            store.dispatch('setDxpServiceProviderConfigurationDisplayNames', dxpServiceProviderConfigurationDisplayNames)
        }).catch(() => {
        })) // Catch error, so it doesn't get logged to the console.
    }

    return Promise.allSettled(promises)
}

export function getNumberOfMatchesOfType (contractMatchShortlist, customerNameMatch) {
    return contractMatchShortlist.filter(contractMatch => contractMatch.customer_name_match === customerNameMatch).length
}

export function getMatchInfo (targetObject, contractId = null) {
    const { contractMatchShortlist, matchedContract } = targetObject || {}
    const searchContractId = contractId || matchedContract?.id

    let match = null
    if (contractMatchShortlist && searchContractId) {
        match = contractMatchShortlist.find(item => item.contract?.id === searchContractId)
    }

    const info = { matchedContractIsInShortlist: false }
    if (match) {
        const keys = [
            'customer_name_match',
            'is_fuzzy_customer_name_match',
            'is_swapped_name_parts_match',
            'first_name_similarity_score',
            'last_name_similarity_score',
            'company_name_similarity_score',
        ]
        Object.assign(info, pick(match, keys))
        info.matchedContractIsInShortlist = true
    }

    // Highlight config
    // Returns how the name match info should be highlighted in the UI
    let highlightMode = 'none'

    // Highlight everything
    // - customer name match = NONE
    // - customer name match = FULL AND is_swapped_name_parts_match
    if (
        info.customer_name_match === DxpCustomerNameMatch.NONE
        ||
        info.customer_name_match === DxpCustomerNameMatch.FULL && info.is_swapped_name_parts_match
    ) {
        highlightMode = 'full'
    }

    // Highlight partially
    // This is used when a PERSON has a partial match (either first or last name). In the template, it is used in combination with
    // the corresponding name part similarity score to highlight that whole part if the score is below the "somewhat similar" threshold.
    else if (
        info.customer_name_match === DxpCustomerNameMatch.PARTIAL
    ) {
        highlightMode = 'partial'
    }

    // Highlight diff
    // - matched contract AND (any) customer name match
    // - contract NOT confirmed AND in shortlist
    else if (
        targetObject?.matchedContract && info.customer_name_match !== DxpCustomerNameMatch.NONE && info.matchedContractIsInShortlist
        ||
        !targetObject?.matched_contract_confirmed && info.matchedContractIsInShortlist
    ) {
        highlightMode = 'diff'
    }

    info.similarity = {
        highlightMode: highlightMode,
        firstNameSimilarityLevel: match ? getSimilarityLevel(match.first_name_similarity_score) : null,
        lastNameSimilarityLevel: match ? getSimilarityLevel(match.last_name_similarity_score) : null,
        companyNameSimilarityLevel: match ? getSimilarityLevel(match.company_name_similarity_score) : null,
    }

    return info
}

export function checkContactTypeMismatch (targetObject, contract) {
    return (
        contract.customer?.type === ContactType.PERSON && targetObject.customer_company_name
        ||
        contract.customer?.type === ContactType.COMPANY && (targetObject.customer_first_name || targetObject.customer_last_name)
    )
}

